import { useQuery } from '@pinia/colada'

import axios from 'axios'

import useOuraAuth from '#web-oura-tools/auth'

import { useAxios } from '#vue-shared-lib/composables/axios'

import type { OcpStatusResponse, OcpWhoamiResponse } from '#ourahub-intra/types/ocp'

const ouraAuth = useOuraAuth()

const { axiosClient } = useAxios({
  agent: 'ourahub-intra',
  url: import.meta.env.VITE_OCP_API_URL,
  version: import.meta.env.VITE_APP_VERSION,
})

const queryKeysOcp = {
  whoami: (uid: string, env: string) => ['ocp', 'whoami', uid, env] as const,
  status: (uid: string, env: string, service: string) => ['ocp', 'status', uid, env, service] as const,
}

// The OCP API sits behind Twingate on the management clusters, so a failure here is
// usually the network rather than auth. Errors surface on the query instead of being
// swallowed, so callers can tell the two apart.

export const useOcpWhoamiQuery = () =>
  useQuery({
    enabled: () => !!ouraAuth.user.value,
    key: () => queryKeysOcp.whoami(ouraAuth.user.value?.id ?? '', ouraAuth.appEnv.value),
    query: async (): Promise<OcpWhoamiResponse> => {
      const { data } = await axiosClient.get<OcpWhoamiResponse>('/whoami')

      return data
    },
  })

export const useOcpStatusQuery = (service: string) =>
  useQuery({
    enabled: () => !!ouraAuth.user.value && !service.startsWith('outo-'),
    key: () => queryKeysOcp.status(ouraAuth.user.value?.id ?? '', ouraAuth.appEnv.value, service),
    query: async (): Promise<OcpStatusResponse> => {
      const { data } = await axiosClient.get<OcpStatusResponse>(`/status/${service}`)

      return data
    },
  })

/** A short reason for a failed OCP API call, telling the network apart from auth. */
export function describeOcpError(error: unknown): string {
  if (!axios.isAxiosError(error)) {
    return error instanceof Error ? error.message : String(error)
  }

  const status = error.response?.status

  if (status === undefined) return 'Unreachable, check Twingate'
  if (status === 401) return 'Sign-in token rejected'
  if (status === 403) return 'Account not allowed'

  return `HTTP ${status}`
}
